import { useCallback, useEffect, useState } from "react";
import { X, ZoomIn } from "lucide-react";
import { Card } from "./ui/Card";
import Spinner from "./ui/Spinner";
import fetchWithCredentials, {
  isPresignedUrl,
  refreshPresignedFileUrl,
  regenerateFileUrl,
} from "apiClient";
import { CaseImageType, CondoCaseImage } from "types/CondoCase";
import { formatTimestamp, Locale } from "utils/dateFormat";
import { useAuth } from "context/AuthContext";

interface CaseImagesProps {
  caseId: number;
  embedded?: boolean;
}

const TYPE_LABELS_ES: Record<CaseImageType, string> = {
  issue: "Problema",
  ok: "OK",
};

const TYPE_LABELS_EN: Record<CaseImageType, string> = {
  issue: "Issue",
  ok: "OK",
};

const TYPE_BADGE_CLASSES: Record<CaseImageType, string> = {
  issue: "bg-red-100 text-red-800",
  ok: "bg-green-100 text-green-800",
};

export function CaseImages({ caseId, embedded = false }: CaseImagesProps) {
  const [images, setImages] = useState<CondoCaseImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<CondoCaseImage | null>(null);
  const { lang } = useAuth();
  const locale: Locale = lang === "es" ? "es" : "en";
  const typeLabels = locale === "es" ? TYPE_LABELS_ES : TYPE_LABELS_EN;
  const tr = useCallback(
    (es: string, en: string) => (locale === "es" ? es : en),
    [locale],
  );

  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      setError(null);
      try {
        const imagesData: CondoCaseImage[] = await fetchWithCredentials(
          `/manager-api/cases/${caseId}/images/`,
        );
        setImages(imagesData);
      } catch (err: any) {
        console.error(err);
        setError(
          err.message ||
            tr(
              "Error al cargar las fotos del incidente",
              "Failed to load incident photos",
            ),
        );
      } finally {
        setLoading(false);
      }
    };

    if (caseId) {
      fetchImages();
    }
  }, [caseId, tr]);

  if (loading) {
    return embedded ? (
      <div className="flex justify-center p-4">
        <Spinner />
      </div>
    ) : (
      <Card className="flex shrink-0 justify-center p-4">
        <Spinner />
      </Card>
    );
  }

  if (error) {
    return embedded ? (
      <div className="text-sm text-red-500">{error}</div>
    ) : (
      <Card className="shrink-0 p-4">
        <div className="text-sm text-red-500">{error}</div>
      </Card>
    );
  }

  if (images.length === 0) {
    const emptyMessage = tr(
      "No hay fotos disponibles",
      "No photos available",
    );
    if (embedded) {
      return <div className="text-sm text-gray-500">{emptyMessage}</div>;
    }
    return null;
  }

  const imageGrid = (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {images.map((image) => (
        <CaseImageThumbnail
          key={image.id}
          image={image}
          typeLabel={typeLabels[image.type]}
          locale={locale}
          onSelect={() => setSelectedImage(image)}
        />
      ))}
    </div>
  );

  if (embedded) {
    return (
      <>
        <div className="h-full min-h-0 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]">
          {imageGrid}
        </div>
        {selectedImage && (
          <CaseImageLightbox
            image={selectedImage}
            typeLabel={typeLabels[selectedImage.type]}
            locale={locale}
            onClose={() => setSelectedImage(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Card className="flex shrink-0 flex-col gap-3 p-4">
        <h3 className="text-lg font-semibold text-gray-800">
          {tr("Fotos", "Photos")}
        </h3>
        {imageGrid}
      </Card>

      {selectedImage && (
        <CaseImageLightbox
          image={selectedImage}
          typeLabel={typeLabels[selectedImage.type]}
          locale={locale}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </>
  );
}

function CaseImageThumbnail({
  image,
  typeLabel,
  locale,
  onSelect,
}: {
  image: CondoCaseImage;
  typeLabel: string;
  locale: Locale;
  onSelect: () => void;
}) {
  const [fileUrl, setFileUrl] = useState(image.fileUrl);
  const [regeneratingUrl, setRegeneratingUrl] = useState(false);

  useEffect(() => {
    let cancelled = false;
    refreshPresignedFileUrl(image.fileUrl).then((freshUrl) => {
      if (!cancelled) {
        setFileUrl(freshUrl);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [image.fileUrl]);

  const handleImageError = async () => {
    if (!isPresignedUrl(fileUrl) || regeneratingUrl) {
      return;
    }
    setRegeneratingUrl(true);
    try {
      const newUrl = await regenerateFileUrl(fileUrl);
      if (newUrl) {
        setFileUrl(newUrl);
      }
    } catch (err) {
      console.error("Failed to regenerate file URL:", err);
    } finally {
      setRegeneratingUrl(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onSelect}
      className="group relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50 text-left transition hover:border-blue-300 hover:shadow-sm"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-gray-100">
        <img
          src={fileUrl}
          alt={image.title || typeLabel}
          className="h-full w-full object-cover transition group-hover:scale-[1.02]"
          onError={handleImageError}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition group-hover:bg-black/10">
          <ZoomIn className="h-6 w-6 text-white opacity-0 drop-shadow transition group-hover:opacity-100" />
        </div>
      </div>
      <div className="p-2">
        {image.title ? (
          <p className="mb-1 truncate text-sm font-medium text-gray-800">
            {image.title}
          </p>
        ) : null}
        <div className="flex items-center justify-between gap-2">
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium ${TYPE_BADGE_CLASSES[image.type]}`}
          >
            {typeLabel}
          </span>
          <span className="text-xs text-gray-500">
            {formatTimestamp(image.createdAt, locale)}
          </span>
        </div>
      </div>
      {regeneratingUrl && (
        <div className="px-2 pb-2 text-xs text-gray-500">
          {locale === "es" ? "Regenerando URL..." : "Regenerating URL..."}
        </div>
      )}
    </button>
  );
}

function CaseImageLightbox({
  image,
  typeLabel,
  locale,
  onClose,
}: {
  image: CondoCaseImage;
  typeLabel: string;
  locale: Locale;
  onClose: () => void;
}) {
  const [fileUrl, setFileUrl] = useState(image.fileUrl);
  const [regeneratingUrl, setRegeneratingUrl] = useState(false);

  useEffect(() => {
    let cancelled = false;
    refreshPresignedFileUrl(image.fileUrl).then((freshUrl) => {
      if (!cancelled) {
        setFileUrl(freshUrl);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [image.fileUrl]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  const handleImageError = async () => {
    if (!isPresignedUrl(fileUrl) || regeneratingUrl) {
      return;
    }
    setRegeneratingUrl(true);
    try {
      const newUrl = await regenerateFileUrl(fileUrl);
      if (newUrl) {
        setFileUrl(newUrl);
      }
    } catch (err) {
      console.error("Failed to regenerate file URL:", err);
    } finally {
      setRegeneratingUrl(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-black/50 p-2 text-white transition hover:bg-black/70"
        aria-label={locale === "es" ? "Cerrar" : "Close"}
      >
        <X className="h-5 w-5" />
      </button>
      <div
        className="flex max-h-full max-w-5xl flex-col gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={fileUrl}
          alt={image.title || typeLabel}
          className="max-h-[80vh] w-full rounded-lg object-contain"
          onError={handleImageError}
        />
        <div className="flex flex-col gap-1 text-white">
          {image.title ? (
            <p className="text-sm font-medium text-white">{image.title}</p>
          ) : null}
          <div className="flex items-center justify-between gap-3">
            <span
              className={`rounded px-2 py-1 text-sm font-medium ${TYPE_BADGE_CLASSES[image.type]}`}
            >
              {typeLabel}
            </span>
            <span className="text-sm text-gray-200">
              {formatTimestamp(image.createdAt, locale)}
            </span>
          </div>
        </div>
        {regeneratingUrl && (
          <div className="text-sm text-gray-200">
            {locale === "es" ? "Regenerando URL..." : "Regenerating URL..."}
          </div>
        )}
      </div>
    </div>
  );
}
